import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { apiFetch } from '@/lib/apiFetch'

interface FamilyMemberDetail {
  id: number
  parentId: number
  email: string
  firstName: string
  lastName: string
  role: 'ADMIN' | 'MEMBER'
}

interface FamilyChildDetail {
  child: any
  kidRole: 'KID_ADMIN' | 'KID_VIEWER'
}

interface PendingInvite {
  id: number
  email: string
  familyRole: string
  status: string
}

interface Family {
  id: number
  name: string
  requireSignoff: boolean
  role: 'ADMIN' | 'MEMBER'
  members: FamilyMemberDetail[]
  children: FamilyChildDetail[]
  pendingInvites: PendingInvite[]
}

interface ParentUser {
  type: 'parent'
  parent: any & { isAdmin?: boolean }
  children: any[]
  families: Family[]
}

interface ChildUser {
  type: 'child'
  child: any
}

type User = ParentUser | ChildUser | null

interface AuthContextType {
  user: User
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  unifiedLogin: (identifier: string, password: string) => Promise<void>
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>
  childLogin: (username: string, password: string) => Promise<void>
  logout: () => void
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>(null!)

export function AuthProvider({ children: reactChildren }: { children: ReactNode }) {
  const [user, setUser] = useState<User>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch('/app/me')
      setUser(data)
    } catch {
      setUser(null)
    }
  }, [])

  useEffect(() => {
    refresh().finally(() => setLoading(false))
  }, [refresh])

  const login = async (email: string, password: string) => {
    await apiFetch('/app/authenticate', {
      method: 'POST',
      body: JSON.stringify({ emailAddress: email, password }),
    })
    await refresh()
  }

  const signup = async (email: string, password: string, firstName: string, lastName: string) => {
    await apiFetch('/app/signup', {
      method: 'POST',
      body: JSON.stringify({ emailAddress: email, password, firstName, lastName }),
    })
    await refresh()
  }

  const childLogin = async (username: string, password: string) => {
    await apiFetch('/app/child/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    await refresh()
  }

  const unifiedLogin = async (identifier: string, password: string) => {
    await apiFetch('/app/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    })
    await refresh()
  }

  const logout = async () => {
    try { await fetch('/api/app/session', { method: 'DELETE', credentials: 'include' }) } catch {}
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, unifiedLogin, signup, childLogin, logout, refresh }}>
      {reactChildren}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

export type { Family, FamilyMemberDetail, FamilyChildDetail, PendingInvite, ParentUser }
